/**
 * Legal Document Client using tRPC
 *
 * Fetches legal documents (Terms, Privacy) from the database.
 * Uses the current i18n language and falls back to English if not available.
 */

import { useTranslation } from 'react-i18next'
import { trpc } from '@/trpc'

/**
 * Legal document from database
 */
export interface PublicLegalDocument {
  id: string
  type: string
  language: string | null
  title: string
  content: string | null
  version: number
  publishedAt: Date | null
}

/**
 * Hook to fetch a legal document by type
 *
 * Automatically uses the current i18n language.
 * Falls back to English if the document is not available in the requested language.
 *
 * @param type - Document type ('terms' or 'privacy')
 * @returns React Query result with document data
 *
 * @example
 * ```tsx
 * function TermsPage() {
 *   const { data, isLoading, error } = useLegalDocument('terms')
 *
 *   if (isLoading) return <Loading />
 *   if (error || !data) return <Error />
 *
 *   return <div dangerouslySetInnerHTML={{ __html: data.content }} />
 * }
 * ```
 */
export function useLegalDocument(type: 'terms' | 'privacy') {
  const { i18n } = useTranslation()

  return trpc.gdpr.getPublicDocument.useQuery(
    {
      type,
      language: i18n.language,
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    }
  )
}
